import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  createRepairAction,
  deleteRepairAction,
  getAllRepairActions,
  updateRepairAction,
} from '@/services/rc/repair-actions.service'
import type { IRepairActionFilters } from '@/types/rc/filters'
import type { IRepairActionForm } from '@/types/rc/forms'
import type { PartType } from '@/types/rc/miners/PartType'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import type { ComputedRef } from 'vue'

export const useRepairActions = () => {
  const queryClient = useQueryClient()

  const { data: repairActions } = useQuery({
    queryKey: ['repair-actions'],
    queryFn: () => getAllRepairActions().catch(NotifyErrorWrapper('Failed to fetch possible repair actions')),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const getRepairActionsQuery = (filters: IRepairActionFilters) =>
    useQuery({
      queryKey: ['repair-actions', filters],
      queryFn: async () => await getAllRepairActions(filters),
    })

  const deleteRepairActionMutation = useMutation({
    mutationFn: async (id: number) => await deleteRepairAction(id),
    onSuccess: () => {
      NotifySuccess(`Repair Action deleted successfully.`)
      queryClient.invalidateQueries({ queryKey: ['repair-actions'] })
    },
    onError: (e: any) => {
      ElMessage.error(e.message)
    },
  })

  const createRepairActionMutation = useMutation({
    mutationFn: async (form: IRepairActionForm) => await createRepairAction(form),
    onSuccess: () => {
      NotifySuccess(`Repair Action created successfully.`)
      queryClient.invalidateQueries({ queryKey: ['repair-actions'] })
    },
    onError: (e: any) => {
      ElMessage.error(e.message)
    },
  })

  const updateRepairActionMutation = useMutation({
    mutationFn: async ({ repairActionID, form }: { repairActionID: number; form: IRepairActionForm }) =>
      await updateRepairAction(repairActionID, form),
    onSuccess: () => {
      NotifySuccess('Successfully updated repair action.')
      queryClient.invalidateQueries({ queryKey: ['repair-actions'] })
    },
  })

  /**
   * Gets the repair actions for a given part type.
   */
  const getRepairActions = (partType: ComputedRef<PartType>) =>
    repairActions.value?.filter((repairAction) => repairAction.part_type === partType.value)

  /**
   * Gets the failure types for a given part type, formatted for an ElSelect dropdown.
   */
  const getRepairActionsOptions = (partType: ComputedRef<PartType>) =>
    getRepairActions(partType)?.map((repairAction) => ({ value: repairAction, label: repairAction.action }))

  /**
   * Get the failure description for a given failure type ID.
   */
  const getRepairActionDescription = (repairActionId: number) =>
    repairActions.value?.find((ra) => ra.id === repairActionId)?.action ?? null

  return {
    getRepairActions,
    getRepairActionsQuery,
    getRepairActionsOptions,
    getRepairActionDescription,
    deleteRepairActionMutation,
    createRepairActionMutation,
    updateRepairActionMutation,
  }
}
